import type {
  Capability,
  IMUResponse,
  MapCatalogEntry,
  PlatformConfig,
  PointCloudFrame,
  Point3D,
  ReplayFrame,
  ReplaySourceFormat,
  SessionManifest,
  SessionSummary,
  SignedUploadRequest,
  SourceJob,
  StaticMapPointCloud,
  UploadPlan,
} from './types'

async function getJSON<T>(path: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(path, { signal, headers: { Accept: 'application/json' } })
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: response.statusText }))
    throw new Error(body.error || `HTTP ${response.status}`)
  }
  return response.json() as Promise<T>
}

async function adminJSON<T>(path: string, token: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  const response = await fetch(path, {
    method: body === undefined ? 'GET' : 'POST',
    signal,
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  })
  if (!response.ok) {
    const payload = await response.json().catch(() => ({ error: response.statusText }))
    throw new Error(payload.error || `HTTP ${response.status}`)
  }
  if (response.status === 204) return undefined as T
  return response.json() as Promise<T>
}

// The canonical API emits collections as JSON arrays. Keep this boundary
// normalizer as defense-in-depth for legacy adapters or rolling deployments:
// a missing optional frame must render as an empty layer, never crash React.
export function normalizeReplayFrame(frame: ReplayFrame): ReplayFrame {
  return {
    ...frame,
    selectedNs: frame.selectedNs ?? {},
    historyPath: { ...frame.historyPath, points: frame.historyPath.points ?? [] },
    globalPath: { ...frame.globalPath, points: frame.globalPath.points ?? [] },
    localPlannedPath: { ...frame.localPlannedPath, points: frame.localPlannedPath.points ?? [] },
    localExecutedPath: { ...frame.localExecutedPath, points: frame.localExecutedPath.points ?? [] },
    laser: {
      ...frame.laser,
      ranges: frame.laser.ranges ?? [],
      intensities: frame.laser.intensities ?? [],
      flags: frame.laser.flags ?? [],
    },
    rangeSensors: frame.rangeSensors ?? [],
    cameras: frame.cameras ?? [],
    alarms: frame.alarms ?? [],
    grids: (frame.grids ?? []).map((grid) => ({ ...grid, cells: grid.cells ?? [] })),
    strategyPoses: (frame.strategyPoses ?? []).map((pose) => ({ ...pose, trail: pose.trail ?? [] })),
    trajectories: (frame.trajectories ?? []).map((candidate) => ({ ...candidate, points: candidate.points ?? [] })),
    peerRobots: (frame.peerRobots ?? []).map((peer) => ({ ...peer, plannedPath: peer.plannedPath ?? [] })),
  }
}

export const api = {
  config: (signal?: AbortSignal) => getJSON<PlatformConfig>('/api/v1/config', signal),
  capabilities: (signal?: AbortSignal) => getJSON<Capability[]>('/api/v1/capabilities', signal),
  sessions: (signal?: AbortSignal) => getJSON<SessionSummary[]>('/api/v1/sessions', signal),
  maps: (signal?: AbortSignal) => getJSON<MapCatalogEntry[]>('/api/v1/maps', signal),
  manifest: (sessionID: string, signal?: AbortSignal) => getJSON<SessionManifest>(`/api/v1/sessions/${encodeURIComponent(sessionID)}/manifest`, signal),
  frame: (sessionID: string, at: string, signal?: AbortSignal) =>
    getJSON<ReplayFrame>(`/api/v1/sessions/${encodeURIComponent(sessionID)}/frame?at=${encodeURIComponent(at)}`, signal).then(normalizeReplayFrame),
  imu: (sessionID: string, from: string, to: string, pixels: number, signal?: AbortSignal) =>
    getJSON<IMUResponse>(
      `/api/v1/sessions/${encodeURIComponent(sessionID)}/imu?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&pixels=${pixels}`,
      signal,
    ),
  pointCloud: (sessionID: string, at: string, maxPoints: number, signal?: AbortSignal) =>
    getJSON<PointCloudFrame>(`/api/v1/sessions/${encodeURIComponent(sessionID)}/pointcloud?at=${encodeURIComponent(at)}&maxPoints=${maxPoints}`, signal),
  mapPointCloud: async (catalogId: string, mapId: number, maxPoints: number, signal?: AbortSignal): Promise<StaticMapPointCloud> => {
    const response = await fetch(
      `/api/v1/maps/${mapId}/pointcloud?catalogId=${encodeURIComponent(catalogId)}&maxPoints=${maxPoints}`,
      { signal, headers: { Accept: 'application/octet-stream' } },
    )
    if (!response.ok) {
      const body = await response.json().catch(() => ({ error: response.statusText }))
      throw new Error(body.error || `HTTP ${response.status}`)
    }
    const values = new Float32Array(await response.arrayBuffer())
    if (values.length % 3 !== 0) throw new Error('静态地图点云不是有效的 xyz float32 数据')
    const points: Point3D[] = new Array(values.length / 3)
    for (let index = 0; index < points.length; index += 1) {
      points[index] = { x: values[index * 3], y: values[index * 3 + 1], z: values[index * 3 + 2], intensity: 0 }
    }
    return { mapId, points }
  },
  sourceJobs: (token: string, signal?: AbortSignal) => adminJSON<SourceJob[]>('/api/v1/admin/sources', token, undefined, signal),
  activateSource: (
    token: string,
    source: { format: ReplaySourceFormat; objectKeys: string[]; name?: string; robotId?: string; siteName?: string; mapCatalogId?: string },
    signal?: AbortSignal,
  ) => adminJSON<SourceJob>('/api/v1/admin/sources', token, source, signal),
  createUpload: (token: string, file: Pick<File, 'name' | 'size' | 'type'>, format: ReplaySourceFormat, signal?: AbortSignal) =>
    adminJSON<UploadPlan>('/api/v1/admin/uploads/presign', token, {
      filename: file.name,
      contentType: file.type || 'application/octet-stream',
      format,
      size: file.size,
    }, signal),
  signMultipartPart: (token: string, objectKey: string, uploadId: string, partNumber: number, signal?: AbortSignal) =>
    adminJSON<{ partNumber: number; upload: SignedUploadRequest }>('/api/v1/admin/uploads/multipart/part', token, { objectKey, uploadId, partNumber }, signal),
  completeMultipart: (token: string, objectKey: string, uploadId: string, parts: { partNumber: number; etag: string }[], signal?: AbortSignal) =>
    adminJSON('/api/v1/admin/uploads/multipart/complete', token, { objectKey, uploadId, parts }, signal),
  abortMultipart: (token: string, objectKey: string, uploadId: string, signal?: AbortSignal) =>
    adminJSON<void>('/api/v1/admin/uploads/multipart/abort', token, { objectKey, uploadId }, signal),
}
